/**
 * Centralized utility for phone number normalization and comparison.
 *
 * Coordinator / auth paths use normalizePhone (Israel-only: +972XXXXXXXXX).
 * Guest messaging paths use normalizeGuestPhone (any valid E.164, default region IL).
 */

import { parsePhoneNumberFromString } from 'libphonenumber-js';

const ARABIC_INDIC_MAP = {
  '\u0660': '0',
  '\u0661': '1',
  '\u0662': '2',
  '\u0663': '3',
  '\u0664': '4',
  '\u0665': '5',
  '\u0666': '6',
  '\u0667': '7',
  '\u0668': '8',
  '\u0669': '9',
};

const PERSIAN_MAP = {
  '\u06F0': '0',
  '\u06F1': '1',
  '\u06F2': '2',
  '\u06F3': '3',
  '\u06F4': '4',
  '\u06F5': '5',
  '\u06F6': '6',
  '\u06F7': '7',
  '\u06F8': '8',
  '\u06F9': '9',
};

/**
 * Strip bidi marks, map Eastern digits to Western, keep digits and a single leading +.
 */
function sanitizeGuestPhoneInput(phone) {
  if (!phone || typeof phone !== 'string') return '';

  let s = phone
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '')
    .replace(/＋/g, '+')
    .trim();

  s = s
    .split('')
    .map((c) => ARABIC_INDIC_MAP[c] ?? PERSIAN_MAP[c] ?? c)
    .join('');

  // If the user typed/pasted another +… after leaving +972, keep the last + segment
  // e.g. "+972+14155552671" or "+972 +1 415…" → "+14155552671"
  const plusIndexes = [];
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === '+') plusIndexes.push(i);
  }
  if (plusIndexes.length > 1) {
    s = s.slice(plusIndexes[plusIndexes.length - 1]);
  }

  const wantsPlus = s.includes('+') || s.startsWith('00');
  s = s.replace(/[^\d+]/g, '');
  s = s.replace(/\+/g, '');
  if (s.startsWith('00')) {
    s = s.slice(2);
  }
  if (wantsPlus) {
    s = `+${s}`;
  }
  return s;
}

function tryParseGuest(raw) {
  if (!raw) return null;
  const parsed = parsePhoneNumberFromString(raw, 'IL');
  if (!parsed) return null;
  if (!parsed.isValid() && !parsed.isPossible()) return null;
  return parsed.format('E.164');
}

/**
 * Normalizes a phone number to a consistent format: +972XXXXXXXXX
 * @param {string} phone - The phone number to normalize
 * @returns {string|null} Normalized phone number in format +972XXXXXXXXX, or null if invalid
 */
export const normalizePhone = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return null;
  }

  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');

  // Handle empty result
  if (!cleaned) {
    return null;
  }

  // If starts with +972, remove + and keep 972XXXXXXXXX
  if (cleaned.startsWith('+972')) {
    cleaned = cleaned.substring(1); // Remove +
  }
  // If starts with 972 (without +), keep as is
  else if (cleaned.startsWith('972')) {
    // Already in correct format
  }
  // If starts with 0 (Israeli local format), replace 0 with 972
  else if (cleaned.startsWith('0')) {
    cleaned = '972' + cleaned.substring(1);
  }
  // Otherwise, assume it's missing country code, prepend 972
  else {
    cleaned = '972' + cleaned;
  }

  // Add + prefix
  cleaned = '+' + cleaned;

  // Validate: should be +972 followed by 9 digits (total 13 characters)
  if (cleaned.length === 13 && cleaned.startsWith('+972')) {
    return cleaned;
  }

  // If validation fails, return null
  return null;
};

/**
 * Normalizes a guest phone to E.164.
 * Local numbers without a country code are parsed as IL.
 * Explicit international numbers (e.g. +1..., +44...) are preserved.
 * @param {string} phone
 * @returns {string|null}
 */
export const normalizeGuestPhone = (phone) => {
  let trimmed = sanitizeGuestPhoneInput(phone);
  if (!trimmed || trimmed === '+' || trimmed === '+972') {
    return null;
  }

  // If user kept +972 and typed a local number with leading 0 (+972050...), drop the trunk 0.
  trimmed = trimmed.replace(/^\+9720/, '+972');

  // Explicit country code — parse as international
  if (trimmed.startsWith('+')) {
    return tryParseGuest(trimmed);
  }

  // Israeli local formats (leading 0, or 9-digit national mobile)
  if (trimmed.startsWith('0') || /^5\d{8}$/.test(trimmed)) {
    const local = tryParseGuest(trimmed);
    if (local) return local;
  }

  // US/CA NANP without +: 1 + 10 digits
  if (/^1\d{10}$/.test(trimmed)) {
    const nanp = tryParseGuest(`+${trimmed}`);
    if (nanp) return nanp;
  }

  // Other bare international digits (country code included, no +)
  if (trimmed.length >= 10) {
    const intl = tryParseGuest(`+${trimmed}`);
    if (intl) return intl;
  }

  // Fallback: IL default region
  return tryParseGuest(trimmed);
};

/**
 * Compares two phone numbers after Israel-only normalization.
 * @param {string} phone1
 * @param {string} phone2
 * @returns {boolean}
 */
export const arePhonesEqual = (phone1, phone2) => {
  if (!phone1 && !phone2) {
    return true;
  }

  if (!phone1 || !phone2) {
    return false;
  }

  const normalized1 = normalizePhone(phone1);
  const normalized2 = normalizePhone(phone2);

  if (!normalized1 || !normalized2) {
    return false;
  }

  return normalized1 === normalized2;
};

/**
 * Compares two guest phones after E.164 normalization (default region IL).
 * @param {string} phone1
 * @param {string} phone2
 * @returns {boolean}
 */
export const areGuestPhonesEqual = (phone1, phone2) => {
  if (!phone1 && !phone2) {
    return true;
  }

  if (!phone1 || !phone2) {
    return false;
  }

  const normalized1 = normalizeGuestPhone(phone1);
  const normalized2 = normalizeGuestPhone(phone2);

  if (!normalized1 || !normalized2) {
    return false;
  }

  return normalized1 === normalized2;
};

/**
 * Normalizes a phone number and throws an error if invalid.
 * Israel-only (coordinator / event creation).
 * @param {string} phone
 * @param {string} paramName
 * @returns {string}
 */
export const normalizePhoneOrThrow = (phone, paramName = 'phone') => {
  const normalized = normalizePhone(phone);
  if (!normalized) {
    throw new Error(`Invalid phone number format: ${phone} (parameter: ${paramName})`);
  }
  return normalized;
};
